'use client';

import { useState, useRef, useEffect } from 'react';
import './SearchableUserSelect.css';

// Mock data - fallback if no getSortedUsers function is provided
const mockUsers = [
  {
    userId: '1',
    userName: 'John Doe',
    userEmail: 'john.doe@example.com',
    totalAssets: 45,
    enabledAssets: 32,
    linkedButtons: 8,
    topicAssets: 12,
    splashAssets: 8,
    menuPageAssets: 15,
    menuContentAssets: 10,
  },
  {
    userId: '2',
    userName: 'Jane Smith',
    userEmail: 'jane.smith@example.com',
    totalAssets: 38,
    enabledAssets: 28,
    linkedButtons: 6,
    topicAssets: 10,
    splashAssets: 6,
    menuPageAssets: 12,
    menuContentAssets: 10,
  },
];

const SearchableUserSelect = ({
  getSortedUsers,
  onUserSelect,
  selectedUserId: externalSelectedUserId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalSelectedUserId, setInternalSelectedUserId] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Use external selectedUserId if provided, otherwise use internal state
  const selectedUserId =
    externalSelectedUserId !== undefined ? externalSelectedUserId : internalSelectedUserId;

  // Get users from the provided function or use mock data
  const getUsers = () => {
    if (getSortedUsers && typeof getSortedUsers === 'function') {
      try {
        return getSortedUsers();
      } catch (error) {
        console.error('Error calling getSortedUsers:', error);
        return mockUsers;
      }
    }
    return mockUsers;
  };

  const filteredUsers = getUsers().filter(
    user =>
      user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedUser = getUsers().find(user => user.userId === selectedUserId);

  const handleUserSelect = userId => {
    // Update internal state if not controlled externally
    if (externalSelectedUserId === undefined) {
      setInternalSelectedUserId(userId);
    }

    setIsOpen(false);
    setSearchTerm('');

    // Call the parent's onUserSelect if provided
    if (onUserSelect) {
      onUserSelect(userId);
    }

    console.log('Selected user:', userId);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="searchable-select" ref={dropdownRef}>
      <div className="select-trigger" onClick={toggleDropdown}>
        {selectedUser ? (
          <div className="selected-user">
            <div className="user-info">
              <div className="user-name">{selectedUser.userName}</div>
              <div className="user-email">{selectedUser.userEmail}</div>
              <div className="user-stats">
                <span className="stat-badge">{selectedUser.totalAssets} assets</span>
                <span className="stat-badge enabled">{selectedUser.enabledAssets} enabled</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="placeholder">
            <span>👤 Select a user...</span>
          </div>
        )}
        <div className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</div>
      </div>

      {isOpen && (
        <div className="dropdown-content">
          <div className="search-container">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="options-container">
            {filteredUsers.length === 0 ? (
              <div className="no-results">No users found</div>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user.userId}
                  className={`option ${selectedUserId === user.userId ? 'selected' : ''}`}
                  onClick={() => handleUserSelect(user.userId)}
                >
                  <div className="option-content">
                    <div className="user-header">
                      <span className="user-icon">👤</span>
                      <span className="user-name">{user.userName}</span>
                      {selectedUserId === user.userId && <span className="check-mark">✓</span>}
                    </div>
                    <div className="user-email">📧 {user.userEmail}</div>

                    <div className="stats-grid">
                      <div className="stat-item">
                        <span className="stat-label">Total:</span>
                        <span className="stat-value">{user.totalAssets}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Enabled:</span>
                        <span className="stat-value enabled">{user.enabledAssets}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Buttons:</span>
                        <span className="stat-value">{user.linkedButtons}</span>
                      </div>
                    </div>

                    <div className="asset-breakdown">
                      <span className="breakdown-item">T: {user.topicAssets}</span>
                      <span className="breakdown-item">S: {user.splashAssets}</span>
                      <span className="breakdown-item">MP: {user.menuPageAssets}</span>
                      <span className="breakdown-item">MC: {user.menuContentAssets}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Selected User Details */}
      {selectedUser && (
        <div className="selected-details">
          <h3>Selected User Details</h3>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Total Assets:</span>
              <span className="detail-value">{selectedUser.totalAssets}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Enabled:</span>
              <span className="detail-value enabled">{selectedUser.enabledAssets}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Linked Buttons:</span>
              <span className="detail-value">{selectedUser.linkedButtons}</span>
            </div>
          </div>

          <div className="breakdown-section">
            <span className="breakdown-title">Asset Breakdown:</span>
            <div className="breakdown-badges">
              <span className="badge">Topic: {selectedUser.topicAssets}</span>
              <span className="badge">Splash: {selectedUser.splashAssets}</span>
              <span className="badge">Menu Page: {selectedUser.menuPageAssets}</span>
              <span className="badge">Menu Content: {selectedUser.menuContentAssets}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableUserSelect;
